import { FunctionComponent, useEffect, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import styles from './editDaycareProfileDialog.module.scss'
import UserInput from '@/components/_custom/UserInput/userInput'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { SelectOptionsDeprecated } from '@/types/Select'
import { DaycareType } from '@/types/Daycare'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useMutation } from '@apollo/client'
import { UPDATE_DAYCARE } from '@/modules/API/daycare/daycareHandler'
import ProfileAddress from '../../../../account/profile/profileAddress/profileAddress'
import { useAuth } from '@/modules/context/AuthContext'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface editDaycareProfileDialogProps {
    open: boolean
    handleClose: () => void
    daycareId: number
    daycareName: string
    setDaycareName: (daycareName: string) => void
    type: string
    setType: (type: DaycareType) => void
    managerName: string
    setManagerName: (managerName: string) => void
    phone: string
    setPhone: (phone: string) => void
    email: string
    setEmail: (email: string) => void
    address: string
    setAddress: (address: string) => void
    city: string
    setCity: (city: string) => void
    zip: string
    setZip: (zip: string) => void
    country: string
    setCountry: (country: string) => void
}

const EditDaycareProfile: FunctionComponent<editDaycareProfileDialogProps> = ({
    open,
    handleClose,
    daycareId,
    daycareName,
    setDaycareName,
    type,
    setType,
    managerName,
    setManagerName,
    phone,
    setPhone,
    email,
    setEmail,
    address,
    setAddress,
    city,
    setCity,
    zip,
    setZip,
    country,
    setCountry
}: editDaycareProfileDialogProps) => {
    const { t } = useTranslation()

    const { fetchCurrentDaycare } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [daycareNameEdit, setDaycareNameEdit] = useState<string>(daycareName)
    const [phoneEdit, setPhoneEdit] = useState<string>(phone)
    const [validPhone, setValidPhone] = useState<boolean>(true)
    const [typeEdit, setTypeEdit] = useState<string>(type)
    const [managerNameEdit, setManagerNameEdit] = useState<string>(managerName)
    const [emailEdit, setEmailEdit] = useState<string>(email)
    const [validEmail, setValidEmail] = useState<boolean>(true)
    const [addressEdit, setAddressEdit] = useState<string>(address)
    const [zipEdit, setZipEdit] = useState<string>(zip)
    const [cityEdit, setCityEdit] = useState<string>(city)
    const [countryEdit, setCountryEdit] = useState<string>(country)

    const [updateDaycare, dataUpdateDaycare] = useMutation(UPDATE_DAYCARE, {
        variables: {
            daycare_id: daycareId,
            name: daycareNameEdit,
            phone: phoneEdit,
            type: typeEdit,
            manager: managerNameEdit,
            email: emailEdit,
            address: addressEdit,
            zip: zipEdit,
            city: cityEdit,
            country: countryEdit
        },
        onCompleted: () => {
            fetchCurrentDaycare()

            setDaycareName(daycareNameEdit)
            setPhone(phoneEdit)
            setType(typeEdit as DaycareType)
            setManagerName(managerNameEdit)
            setEmail(emailEdit)
            setAddress(addressEdit)
            setZip(zipEdit)
            setCity(cityEdit)
            setCountry(countryEdit)

            handleClose()

            showNotificationSuccess(t('daycareUpdated'))
        },
        onError: () => {
            showNotificationError(t('errorUpdateDaycare'))
        }
    })

    const selectDaycareType: SelectOptionsDeprecated = [
        {
            options: [
                { value: DaycareType.daycare, name: t('daycare') },
                { value: DaycareType.nursery, name: t('nursery') },
                { value: DaycareType.toyLibrary, name: t('toyLibrary') },
                { value: DaycareType.microDaycare, name: t('microDaycare') },
                { value: DaycareType.multiHome, name: t('multiHome') },
                { value: DaycareType.recreationCenter, name: t('recreationCenter') },
                { value: DaycareType.playCenter, name: t('playCenter') },
                { value: DaycareType.rpe, name: t('rpe') },
                { value: DaycareType.laep, name: t('laep') },
                { value: DaycareType.other, name: t('other') }
            ]
        }
    ]

    useEffect(() => {
        setDaycareNameEdit(daycareName)
        setPhoneEdit(phone)
        setTypeEdit(type)
        setManagerNameEdit(managerName)
        setEmailEdit(email)
        setAddressEdit(address)
        setZipEdit(zip)
        setCityEdit(city)
        setCountryEdit(country)
    }, [daycareId, daycareName, phone, type, managerName, email, address, zip, city, country])

    const isDataValid: boolean =
        daycareNameEdit !== '' &&
        phoneEdit !== '' &&
        typeEdit !== '' &&
        managerNameEdit !== '' &&
        validPhone &&
        validEmail &&
        emailEdit !== '' &&
        addressEdit !== '' &&
        zipEdit !== '' &&
        cityEdit !== '' &&
        countryEdit !== ''

    const isDataModified: boolean =
        daycareNameEdit !== daycareName ||
        phoneEdit !== phone ||
        typeEdit !== type ||
        managerNameEdit !== managerName ||
        emailEdit !== email ||
        addressEdit !== address ||
        zipEdit !== zip ||
        cityEdit !== city ||
        countryEdit !== country

    const disabled: boolean = !isDataValid || !isDataModified || dataUpdateDaycare.loading

    return (
        <Dialog open={open} handleClose={handleClose} className={styles.Dialog}>
            <DialogHeader title={t('editDaycareProfile')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <div className={styles.Input}>
                        <SelectUnique
                            selectOptions={selectDaycareType}
                            selectValue={typeEdit}
                            setSelectValue={setTypeEdit}
                            className={styles.UserInput}
                            label={t('daycareType')}
                        />
                    </div>
                    <div className={styles.Input}>
                        <UserInput
                            label={t('daycareName')}
                            value={daycareNameEdit}
                            type='text'
                            required
                            fullWidth
                            format={'pascal'}
                            className={styles.UserInput}
                            setValue={setDaycareNameEdit}
                        />
                        <UserInput
                            label={t('managerName')}
                            value={managerNameEdit}
                            type='text'
                            required
                            fullWidth
                            format={'pascal'}
                            className={styles.UserInput}
                            unallowedCharacters={'num'}
                            setValue={setManagerNameEdit}
                        />
                    </div>
                    <div className={styles.Input}>
                        <UserInput
                            label={t('phone')}
                            value={phoneEdit}
                            setValue={setPhoneEdit}
                            type='text'
                            verify={'phone'}
                            required
                            fullWidth
                            className={styles.UserInput}
                            format={'phone'}
                            setValid={setValidPhone}
                        />
                        <UserInput
                            label={t('email')}
                            value={emailEdit}
                            setValue={setEmailEdit}
                            verify={'email'}
                            type='text'
                            unallowedCharacters={'email'}
                            setValid={setValidEmail}
                            required
                            fullWidth
                            className={styles.UserInput}
                        />
                    </div>
                </div>
                <div className={styles.InputContainer}>
                    <ProfileAddress
                        signUp={false}
                        address={addressEdit}
                        setAddress={setAddressEdit}
                        city={cityEdit}
                        setCity={setCityEdit}
                        zip={zipEdit}
                        setZip={setZipEdit}
                        country={countryEdit}
                        setCountry={setCountryEdit}
                    />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'update'}
                        onClick={updateDaycare}
                        className={styles.Button}
                        disabled={disabled}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default EditDaycareProfile
